"use client";

import { FC, useEffect, useMemo } from "react";
import { useKeyPressEvent } from "react-use";
import { usePathname } from "next/navigation";

import { MODALS } from "@Shared/config";
import { useModalState } from "@Shared/store";
import { ElProps } from "@Shared/types";
import { Icon, Image } from "@Shared/ui";
import { twcx } from "@Shared/utils";

import imageBg from "./images/bg.webp";

export type ContainerProps = ElProps<"div"> & {
    onOpen?: () => void;
    onClose?: () => void;
};

export const Container: FC<ContainerProps> = ({ className, onOpen, onClose, children, ...restProps }) => {
    const pathname = usePathname();
    const {
        activeModals,
        create,
        isOpen: modalIsOpen,
        close: modalClose,
    } = useModalState(["activeModals", "create", "isOpen", "close"]);

    const isOpen = useMemo(() => modalIsOpen(MODALS.getInTouch), [activeModals]);

    const close = () => modalClose(MODALS.getInTouch);

    useKeyPressEvent("Escape", () => (isOpen ? close() : null));

    useEffect(() => {
        create(MODALS.getInTouch);
    }, []);

    useEffect(() => {
        if (isOpen) {
            close();
        }
    }, [pathname]);

    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        } else {
            onClose?.();
        }
    }, [isOpen]);

    return (
        <div
            className={twcx(
                "scroll-container-y fixed inset-0 z-40 flex items-start justify-center overflow-hidden bg-bg-black bg-opacity-50 p-4 transition-opacity",
                "md:justify-end md:p-6",
                {
                    "pointer-events-none opacity-0": !isOpen,
                },
                className
            )}
            onClick={close}
            {...restProps}
        >
            <div
                className={twcx(
                    "relative z-0 flex min-h-full w-full flex-col justify-center gap-4 overflow-hidden rounded-[20px] bg-bg-black px-6 py-14 text-white transition-[opacity,transform]",
                    "before:bg-gradient-modal-get-in-touch-bg before:pointer-events-none before:absolute before:bottom-0 before:left-0 before:right-0 before:-z-10 before:h-[258px] before:-scale-y-100",
                    "md:max-w-[350px] md:px-9 md:py-16",
                    "lg:max-w-[576px] lg:gap-5 lg:px-8 lg:py-20",
                    {
                        "scale-75 opacity-0 md:translate-x-full": !isOpen,
                    }
                )}
                onClick={e => e.stopPropagation()}
            >
                <Image
                    className={twcx(
                        "pointer-events-none absolute bottom-0 left-0 -z-20 h-[257px] w-full min-w-[576px] select-none object-cover"
                    )}
                    src={imageBg}
                    alt="Background"
                    role="presentation"
                    sizes={{ default: "92vw", md: "576px" }}
                />

                <button
                    className={twcx(
                        "absolute right-[18px] top-[18px] z-10 flex size-10 items-center justify-center",
                        "md:right-[26px] md:top-[26px]",
                        "lg:transition-colors lg:[&:not(:disabled)]:hover:text-accent-1"
                    )}
                    type="button"
                    aria-label="Close modal Get In Touch"
                    onClick={close}
                >
                    <Icon k="close" />
                </button>

                {children}
            </div>
        </div>
    );
};
